<div class="agent-business-report-container">
  <div class="report-header">
    <h1 class="page-title">Agent Business Report</h1>
    <p class="page-subtitle">View detailed monthly performance reports for agents</p>
  </div>

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filter-card">
      <div class="filter-group">
        <label for="agent-select" class="filter-label">Select Agent</label>
        <select 
          id="agent-select" 
          class="filter-select" 
          [(ngModel)]="selectedAgentId"
          (change)="error = ''">
          <option value="">-- Select Agent --</option>
          <option *ngFor="let agent of agents" [value]="agent.agentId">
            {{ agent.name }} ({{ agent.phoneNumber }})
          </option>
        </select>
      </div>

      <div class="filter-row">
        <div class="filter-group">
          <label for="month-select" class="filter-label">Month</label>
          <select 
            id="month-select" 
            class="filter-select" 
            [(ngModel)]="selectedMonth">
            <option *ngFor="let month of months" [value]="month.value">
              {{ month.name }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label for="year-select" class="filter-label">Year</label>
          <select 
            id="year-select" 
            class="filter-select" 
            [(ngModel)]="selectedYear">
            <option *ngFor="let year of years" [value]="year">
              {{ year }}
            </option>
          </select>
        </div>
      </div>

      <div class="filter-actions">
        <button 
          class="btn btn-primary" 
          (click)="loadReport()"
          [disabled]="loading || !selectedAgentId">
          <span *ngIf="!loading">Generate Report</span>
          <span *ngIf="loading">Loading...</span>
        </button>

        <button 
          class="btn btn-secondary" 
          (click)="exportToPdf()"
          [disabled]="!report || exportingPdf">
          <span *ngIf="!exportingPdf">📄 Export PDF</span>
          <span *ngIf="exportingPdf">Exporting...</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div *ngIf="error" class="error-message">
    <span class="error-icon">⚠️</span>
    <span>{{ error }}</span>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading-container">
    <div class="loader"></div>
    <p>Loading report data...</p>
  </div>

  <!-- Report Content -->
  <div id="report-content" *ngIf="report && !loading" class="report-content">
    
    <!-- Report Title Section -->
    <div class="report-title-section">
      <h2 class="report-title">Monthly Business Report</h2>
      <div class="report-meta">
        <p><strong>Agent:</strong> {{ report.agentName }}</p>
        <p><strong>Period:</strong> {{ report.monthName }} {{ report.year }}</p>
        <p><strong>Report Date Range:</strong> {{ formatDate(report.reportStartDate) }} - {{ formatDate(report.reportEndDate) }}</p>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="summary-grid">
      <div class="summary-card primary">
        <div class="card-icon">📦</div>
        <div class="card-content">
          <h3 class="card-value">{{ report.totalPickups }}</h3>
          <p class="card-label">Total Pickups</p>
        </div>
      </div>

      <div class="summary-card success">
        <div class="card-icon">✅</div>
        <div class="card-content">
          <h3 class="card-value">{{ report.completedPickups }}</h3>
          <p class="card-label">Completed</p>
        </div>
      </div>

      <div class="summary-card warning">
        <div class="card-icon">❌</div>
        <div class="card-content">
          <h3 class="card-value">{{ report.cancelledAppointments }}</h3>
          <p class="card-label">Cancelled</p>
        </div>
      </div>

      <div class="summary-card danger">
        <div class="card-icon">🚫</div>
        <div class="card-content">
          <h3 class="card-value">{{ report.rejectedAppointments }}</h3>
          <p class="card-label">Rejected</p>
        </div>
      </div>
    </div>

    <!-- Completion Rate -->
    <div class="section-card">
      <h3 class="section-title">Performance Metrics</h3>
      <div class="metrics-grid">
        <div class="metric-item">
          <span class="metric-label">Completion Rate</span>
          <div class="progress-bar">
            <div class="progress-fill" [style.width.%]="getCompletionRate()"></div>
          </div>
          <span class="metric-value">{{ getCompletionRate().toFixed(1) }}%</span>
        </div>
        <div class="metric-item">
          <span class="metric-label">Total Scrap Volume</span>
          <span class="metric-value">{{ report.totalScrapVolumeCollected }} kg</span>
        </div>
      </div>
    </div>

    <!-- Financial Summary -->
    <div class="section-card financial">
      <h3 class="section-title">Financial Summary</h3>
      <div class="financial-grid">
        <div class="financial-item">
          <span class="financial-label">Business Value</span>
          <span class="financial-value">{{ formatCurrency(report.totalBusinessValue) }}</span>
        </div>
        <div class="financial-item">
          <span class="financial-label">Market Value</span>
          <span class="financial-value">{{ formatCurrency(report.totalMarketValue) }}</span>
        </div>
        <div class="financial-item">
          <span class="financial-label">Grand Total</span>
          <span class="financial-value highlight">{{ formatCurrency(report.totalGrandTotal) }}</span>
        </div>
        <div class="financial-item">
          <span class="financial-label">Platform Charges</span>
          <span class="financial-value negative">{{ formatCurrency(report.totalPlatformCharges) }}</span>
        </div>
        <div class="financial-item">
          <span class="financial-label">Collection Charges</span>
          <span class="financial-value">{{ formatCurrency(report.totalCollectionCharges) }}</span>
        </div>
        <div class="financial-item profit">
          <span class="financial-label">Profit</span>
          <span class="financial-value">{{ formatCurrency(report.profitValue) }}</span>
          <span class="profit-percentage">{{ report.profitPercentage.toFixed(2) }}%</span>
        </div>
      </div>
    </div>

    <!-- Subscription Information -->
    <div class="section-card">
      <h3 class="section-title">Subscription Overview</h3>
      <div class="subscription-summary">
        <div class="subscription-stat">
          <span class="stat-value">{{ report.activeSubscriptionsCount }}</span>
          <span class="stat-label">Active Subscriptions</span>
        </div>
        <div class="subscription-stat">
          <span class="stat-value">{{ formatCurrency(report.totalSubscriptionFees) }}</span>
          <span class="stat-label">Total Fees</span>
        </div>
      </div>

      <div *ngIf="report.subscriptionDetails && report.subscriptionDetails.length > 0" class="subscriptions-list">
        <h4 class="subsection-title">Subscription Details</h4>
        <div class="table-responsive">
          <table class="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Cadence</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Period</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let sub of report.subscriptionDetails">
                <td>{{ sub.type }}</td>
                <td>{{ sub.cadence }}</td>
                <td>{{ formatCurrency(sub.amount) }}</td>
                <td><span class="status-badge" [class]="sub.status.toLowerCase()">{{ sub.status }}</span></td>
                <td><span class="status-badge" [class]="sub.paymentStatus.toLowerCase()">{{ sub.paymentStatus }}</span></td>
                <td>{{ formatDate(sub.startDate) }} - {{ formatDate(sub.endDate) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Customer Feedback -->
    <div class="section-card">
      <h3 class="section-title">Customer Feedback</h3>
      <div class="feedback-summary">
        <div class="rating-container">
          <div class="rating-large">{{ report.averageRating.toFixed(1) }}</div>
          <div class="rating-stars">⭐⭐⭐⭐⭐</div>
          <div class="rating-meta">Based on {{ report.totalRatings }} ratings</div>
        </div>
        <div class="feedback-count">
          <span class="count-value">{{ report.feedbackCount }}</span>
          <span class="count-label">Written Feedbacks</span>
        </div>
      </div>

      <div *ngIf="report.customerFeedbacks && report.customerFeedbacks.length > 0" class="feedback-list">
        <h4 class="subsection-title">Customer Comments</h4>
        <div class="feedback-items">
          <div *ngFor="let feedback of report.customerFeedbacks" class="feedback-item">
            <span class="feedback-icon">💬</span>
            <p class="feedback-text">{{ feedback }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Scrap Item Breakdown -->
    <div class="section-card">
      <h3 class="section-title">Scrap Item Breakdown</h3>
      <div *ngIf="report.scrapItemBreakdown && report.scrapItemBreakdown.length > 0" class="table-responsive">
        <table class="data-table">
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Unit</th>
              <th>Total Qty</th>
              <th>Deducted</th>
              <th>Net Qty</th>
              <th>Collections</th>
              <th>Total Value</th>
              <th>Market Value</th>
              <th>Profit</th>
              <th>Profit %</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of report.scrapItemBreakdown">
              <td class="item-name">{{ item.scrapItemName }}</td>
              <td>{{ item.unit }}</td>
              <td>{{ item.totalQuantity }}</td>
              <td class="deducted">{{ item.totalDeductedQuantity }}</td>
              <td class="net-qty">{{ item.netQuantity }}</td>
              <td>{{ item.collectionCount }}</td>
              <td class="value">{{ formatCurrency(item.totalValue) }}</td>
              <td class="value">{{ formatCurrency(item.totalMarketValue) }}</td>
              <td class="value" [class.positive-profit]="item.profitValue >= 0" [class.negative-profit]="item.profitValue < 0">
                {{ formatCurrency(item.profitValue) }}
              </td>
              <td [class.positive-profit]="item.profitPercentage >= 0" [class.negative-profit]="item.profitPercentage < 0">
                {{ item.profitPercentage.toFixed(2) }}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div *ngIf="!report.scrapItemBreakdown || report.scrapItemBreakdown.length === 0" class="empty-state">
        <p>No scrap items collected during this period</p>
      </div>
    </div>

  </div>

  <!-- No Data State -->
  <div *ngIf="!report && !loading && !error" class="empty-state-large">
    <div class="empty-icon">📊</div>
    <h3>No Report Generated</h3>
    <p>Select an agent, month, and year to generate the business report</p>
  </div>
</div>

